import React, { useEffect, useContext, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  MenuItem,
  TextField,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  useTheme,
  Tooltip,
  IconButton,
  TableFooter,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import {
  TaskReportingDashboardClient,
  TaskCategoriesClient,
  TaskPrioritiesClient,
  TaskStatusesClient,
  TaskSummaryDto,
  TaskCategoryDto,
  TaskPriorityDto,
  TaskStatusDto,
  TaskDashboardResponse,
} from '../../app/services/api/tasksClient';
import { UserContext } from '../../components/shared/useUser';
import { TableHeader } from '../../components/forms/TableHeader';

const dashboardClient = TaskReportingDashboardClient;
const categoriesClient = TaskCategoriesClient;
const prioritiesClient = TaskPrioritiesClient;
const statusesClient = TaskStatusesClient;

// Helper to get unique values from array (workaround for Set iteration)
const getUniqueValues = <T,>(arr: T[]): T[] => {
  const seen = new Set<T>();
  return arr.filter(item => {
    if (seen.has(item)) return false;
    seen.add(item);
    return true;
  });
};

interface KPICardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: number;
  trendLabel?: string;
  color?: string;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, subtitle, icon, trend, trendLabel, color }) => {
  const theme = useTheme();
  const trendUp = trend && trend > 0;
  const trendColor = trendUp ? theme.palette.success.main : theme.palette.error.main;

  return (
    <Card sx={{
      height: '100%', border: '1px solid',
      boxShadow: '0 1px 2px 1px #1018280D',
      borderRadius: '12px',
      borderColor: theme.palette.custom.gray[300],
    }}>
      <CardContent>
        <Box display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1.5}>
          {trend !== undefined ? (
            <Box display="flex"
              alignItems="center">
              {trendUp ? (
                <TrendingUpIcon sx={{ fontSize: 16, color: trendColor, mr: 0.5 }} />
              ) : (
                <TrendingDownIcon sx={{ fontSize: 16, color: trendColor, mr: 0.5 }} />
              )}
              <Typography variant="textXS"
                sx={{ color: trendColor, fontWeight: 500 }}>
                {Math.abs(trend)}% {trendLabel || 'vs last period'}
              </Typography>
            </Box>
          ) : (
            <Box />
          )}
          <Box
            sx={{
              p: 1.5,
              borderRadius: '50%',
              height: '36px',
              width: '36px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: color ? `${color}20` : theme.palette.primary.light + '30',
            }}
          >
            {icon}
          </Box>
        </Box>
        <Box>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ fontWeight: 500 }}>
            {title + ' '}
          </Typography>
          <Typography variant="displayMD"
            sx={{ fontWeight: 600, color: color || 'text.primary', mt: 1 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="textXS"
              color="text.secondary">
              {' ' + subtitle}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel"
      hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const TaskReportingDashboard: React.FC = () => {
  const theme = useTheme();
  const { franchises, stores } = useContext(UserContext);

  // State
  const [dashboardData, setDashboardData] = useState<TaskDashboardResponse | null>(null);
  const [allTasks, setAllTasks] = useState<TaskSummaryDto[]>([]);
  const [categories, setCategories] = useState<TaskCategoryDto[]>([]);
  const [priorities, setPriorities] = useState<TaskPriorityDto[]>([]);
  const [statuses, setStatuses] = useState<TaskStatusDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  // Filters
  const [selectedFranchiseId, setSelectedFranchiseId] = useState<number | ''>('');
  const [selectedStoreNumber, setSelectedStoreNumber] = useState<string>('');
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'quarter'>('week');

  // Derived filter options - filter stores by selected franchise
  const filteredStores = useMemo(() => {
    if (!selectedFranchiseId) return stores;
    return stores.filter(s => s.franchiseId === selectedFranchiseId);
  }, [selectedFranchiseId, stores]);

  // Fetch data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [dashboardRes, categoriesRes, prioritiesRes, statusesRes, tasksRes] = await Promise.all([
          dashboardClient.getDashboard(),
          categoriesClient.getCategories(),
          prioritiesClient.getPriorities(),
          statusesClient.getStatuses(),
          dashboardClient.getReportingTasks(),
        ]);

        setDashboardData(dashboardRes.data);
        setCategories(categoriesRes);
        setPriorities(prioritiesRes);
        setStatuses(statusesRes);
        setAllTasks(tasksRes);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Computed metrics
  const filteredTasks = useMemo(() => {
    let tasks = allTasks;
    if (selectedStoreNumber) {
      tasks = tasks.filter(t => t.storeNumber === selectedStoreNumber);
    } else if (selectedFranchiseId) {
      const storeNumbers = stores
        .filter(s => s.franchiseId === selectedFranchiseId)
        .map(s => s.storeNumber);
      tasks = tasks.filter(t => storeNumbers.includes(t.storeNumber));
    }
    return tasks;
  }, [allTasks, selectedFranchiseId, selectedStoreNumber, stores]);

  // Tasks by Category
  const tasksByCategory = useMemo(() => {
    const categoryMap: Record<string, { total: number; completed: number; overdue: number }> = {};
    filteredTasks.forEach(task => {
      const catName = task.categoryName || 'Uncategorized';
      if (!categoryMap[catName]) {
        categoryMap[catName] = { total: 0, completed: 0, overdue: 0 };
      }
      categoryMap[catName].total += 1;
      if (task.statusId === 3) categoryMap[catName].completed += 1;
      if (task.isOverdue) categoryMap[catName].overdue += 1;
    });
    return Object.entries(categoryMap)
      .map(([name, data]) => ({
        name,
        total: data.total,
        completed: data.completed,
        overdue: data.overdue,
        completionRate: data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0,
      }))
      .sort((a, b) => b.total - a.total);
  }, [filteredTasks]);

  // Tasks by Status
  const tasksByStatus = useMemo(() => {
    return statuses.map(status => ({
      name: status.name,
      value: filteredTasks.filter(t => t.statusId === status.id).length,
      statusId: status.id,
    })).filter(entry => entry.value > 0);
  }, [filteredTasks, statuses]);

  // Tasks by Priority
  const tasksByPriority = useMemo(() => {
    return priorities.map(priority => ({
      name: priority.name,
      value: filteredTasks.filter(t => t.priorityId === priority.id).length,
      priorityId: priority.id,
    })).filter(entry => entry.value > 0);
  }, [filteredTasks, priorities]);

  // On-time completion metrics
  const completionMetrics = useMemo(() => {
    const completedTasks = filteredTasks.filter(t => t.statusId === 3);
    const completedOnTime = completedTasks.filter(t => {
      if (!t.dueDateUtc || !t.completedDateUtc) return true;
      return new Date(t.completedDateUtc) <= new Date(t.dueDateUtc);
    });
    const completedLate = completedTasks.length - completedOnTime.length;

    return {
      total: completedTasks.length,
      onTime: completedOnTime.length,
      late: completedLate,
      onTimeRate: completedTasks.length > 0
        ? Math.round((completedOnTime.length / completedTasks.length) * 100)
        : 0,
    };
  }, [filteredTasks]);

  // Store performance
  const storePerformance = useMemo(() => {
    const storeMap: Record<string, { storeName: string; total: number; completed: number; overdue: number }> = {};
    filteredTasks.forEach(task => {
      if (!storeMap[task.storeNumber]) {
        storeMap[task.storeNumber] = {
          storeName: task.storeName || task.storeNumber,
          total: 0,
          completed: 0,
          overdue: 0,
        };
      }
      storeMap[task.storeNumber].total += 1;
      if (task.statusId === 3) storeMap[task.storeNumber].completed += 1;
      if (task.isOverdue) storeMap[task.storeNumber].overdue += 1;
    });

    return Object.entries(storeMap)
      .map(([storeNumber, data]) => ({
        storeNumber,
        storeName: data.storeName,
        total: data.total,
        completed: data.completed,
        overdue: data.overdue,
        completionRate: data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0,
      }))
      .sort((a, b) => b.total - a.total);
  }, [filteredTasks]);

  // Completion trend (simulated 7-day data)
  const completionTrend = useMemo(() => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayStr = date.toLocaleDateString('en-GB', { weekday: 'short' });
      days.push({
        day: dayStr,
        created: Math.floor(Math.random() * 15) + 5,
        completed: Math.floor(Math.random() * 12) + 3,
        overdue: Math.floor(Math.random() * 3),
      });
    }
    return days;
  }, []);

  // Priority escalation data
  const priorityEscalations = useMemo(() => {
    return filteredTasks
      .filter(t => t.priorityId !== t.defaultPriorityId && t.defaultPriorityId)
      .map(t => {
        const currentPriority = priorities.find(p => p.id === t.priorityId)?.name || 'Unknown';
        const defaultPriority = priorities.find(p => p.id === t.defaultPriorityId)?.name || 'Unknown';
        return {
          taskId: t.id,
          title: t.title,
          storeName: t.storeName,
          defaultPriority,
          currentPriority,
          escalated: (t.priorityId || 0) > (t.defaultPriorityId || 0),
        };
      });
  }, [filteredTasks, priorities]);

  // Chart colors
  const COLORS = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
    '#8884d8',
    '#82ca9d',
    '#ffc658',
  ];

  const STATUS_COLORS: Record<string, string> = {
    'Open': theme.palette.info.main,
    'In Progress': theme.palette.warning.main,
    'Completed': theme.palette.success.main,
  };

  const PRIORITY_COLORS: Record<string, string> = {
    'Low': theme.palette.success.main,
    'Medium': theme.palette.warning.main,
    'High': theme.palette.error.main,
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const clearFilters = () => {
    setSelectedFranchiseId('');
    setSelectedStoreNumber('');
  };

  if (loading) {
    return (
      <PageArea>
        <Box display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px">
          <LinearProgress sx={{ width: '50%' }} />
        </Box>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Box display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={3}>
        <PageTitle subTitle="Comprehensive view of task performance across all locations and colleagues">
          Task Reporting Dashboard
        </PageTitle>
        <Box display="flex"
          gap={1}>
          <Tooltip title="Download report">
            <IconButton onClick={() => console.log('Download report')}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh data">
            <IconButton onClick={() => window.location.reload()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Box display="flex"
        flexWrap="wrap"
        gap={2}
        sx={{ mb: 4, mt: 2 }}
        alignItems="center"
        justifyContent="space-between">
        <Box display="flex"
          flexWrap="wrap"
          gap={2}
          alignItems="top">
          <TextField
            select
            label="Franchise"
            value={selectedFranchiseId}
            onChange={(e) => {
              setSelectedFranchiseId(e.target.value === '' ? '' : Number(e.target.value));
              setSelectedStoreNumber('');
            }}
            size="small"
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All Franchises</MenuItem>
            {franchises.map(f => (
              <MenuItem key={f.franchiseId}
                value={f.franchiseId}>{f.franchiseName}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Store"
            value={selectedStoreNumber}
            onChange={(e) => setSelectedStoreNumber(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All Stores</MenuItem>
            {filteredStores.map(s => (
              <MenuItem key={s.storeNumber}
                value={s.storeNumber}>
                {s.storeName} ({s.storeNumber})
              </MenuItem>
            ))}
          </TextField>

          {(selectedFranchiseId || selectedStoreNumber) && (
            <Chip label="Clear Filters"
              onClick={clearFilters}
              onDelete={clearFilters}
              size="small" />
          )}
        </Box>

        <ToggleButtonGroup
          value={dateRange}
          exclusive
          onChange={(_e, value) => value && setDateRange(value)}
          size="small"
        >
          <ToggleButton value="today">Today</ToggleButton>
          <ToggleButton value="week">This Week</ToggleButton>
          <ToggleButton value="month">This Month</ToggleButton>
          <ToggleButton value="quarter">This Quarter</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* KPI Cards */}
      <Grid container
        spacing={2}
        mb={6}>
        <Grid item
          xs={12}
          sm={6}
          md={3}>
          <KPICard
            title="Total Tasks"
            value={filteredTasks.length}
            subtitle="Including completed"
            icon={<AssignmentIcon sx={{ color: theme.palette.primary.main }} />}
            trend={8}
            trendLabel="vs last week"
          />
        </Grid>
        <Grid item
          xs={12}
          sm={6}
          md={3}>
          <KPICard
            title="Completed"
            value={filteredTasks.filter(t => t.statusId === 3).length}
            subtitle={`${Math.round((filteredTasks.filter(t => t.statusId === 3).length / filteredTasks.length) * 100) || 0}% completion rate`}
            icon={<CheckCircleOutlineIcon sx={{ color: theme.palette.success.main }} />}
            color={theme.palette.success.main}
            trend={12}
          />
        </Grid>
        <Grid item
          xs={12}
          sm={6}
          md={3}>
          <KPICard
            title="On-Time Rate"
            value={`${completionMetrics.onTimeRate}%`}
            subtitle={`${completionMetrics.onTime} of ${completionMetrics.total} tasks`}
            icon={<AccessTimeIcon sx={{ color: theme.palette.info.main }} />}
            color={theme.palette.info.main}
            trend={5}
          />
        </Grid>
        <Grid item
          xs={12}
          sm={6}
          md={3}>
          <KPICard
            title="Overdue"
            value={filteredTasks.filter(t => t.isOverdue).length}
            subtitle="Require attention"
            icon={<WarningAmberIcon sx={{ color: theme.palette.error.main }} />}
            color={theme.palette.error.main}
            trend={-15}
            trendLabel="improvement"
          />
        </Grid>
      </Grid>

      {/* Tabs for different views */}
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{ borderBottom: 1, borderColor: 'divider', px: 2, ml: 4, mr: 4, mb: '-1px' }}
      >
        <Tab label="Overview" />
        <Tab label="By Category" />
        <Tab label="By Location" />
        <Tab label="Trends & Insights" />
      </Tabs>
      <Section>


        {/* Overview Tab */}
        <TabPanel value={tabValue}
          index={0}>
          <Box
            sx={{ padding: 8 }}>
            <Grid container
              spacing={3}>
              {/* Tasks by Status */}
              <Grid item
                xs={12}
                md={4}>
                <Typography variant="textMD"
                  fontWeight={600}
                  gutterBottom>
                  Tasks by Status
                </Typography>
                <Box height={250}>
                  <ResponsiveContainer width="100%"
                    height="100%">
                    <PieChart>
                      <Pie
                        data={tasksByStatus}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={{ strokeWidth: 1 }}
                        style={{ fontSize: 10 }}
                      >
                        {tasksByStatus.map((entry) => (
                          <Cell
                            key={`cell-${entry.statusId}`}
                            fill={STATUS_COLORS[entry.name] || COLORS[entry.statusId % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>

              {/* Tasks by Priority */}
              <Grid item
                xs={12}
                md={4}>
                <Typography variant="textMD"
                  fontWeight={600}
                  gutterBottom>
                  Tasks by Priority
                </Typography>
                <Box height={250}>
                  <ResponsiveContainer width="100%"
                    height="100%">
                    <BarChart data={tasksByPriority}
                      layout="vertical">
                      <CartesianGrid strokeDasharray="3 3"
                        stroke={theme.palette.divider} />
                      <XAxis type="number"
                        tick={{ fontSize: 10 }} />
                      <YAxis dataKey="name"
                        type="category"
                        width={60}
                        tick={{ fontSize: 10 }} />
                      <RechartsTooltip />
                      <Bar dataKey="value"
                        radius={[0, 4, 4, 0]}>
                        {tasksByPriority.map((entry) => (
                          <Cell
                            key={`cell-${entry.priorityId}`}
                            fill={PRIORITY_COLORS[entry.name] || COLORS[entry.priorityId % COLORS.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>

              {/* On-Time Completion */}
              <Grid item
                xs={12}
                md={4}>
                <Typography variant="textMD"
                  fontWeight={600}
                  gutterBottom>
                  Completion Performance
                </Typography>
                <Box height={250}>
                  <ResponsiveContainer width="100%"
                    height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'On Time', value: completionMetrics.onTime },
                          { name: 'Late', value: completionMetrics.late },
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={{ strokeWidth: 1 }}
                        style={{ fontSize: 10 }}
                      >
                        <Cell fill={theme.palette.success.main} />
                        <Cell fill={theme.palette.error.main} />
                      </Pie>
                      <RechartsTooltip />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>

              {/* Completion Trend */}
              <Grid item
                xs={12}>
                <Typography variant="textMD"
                  fontWeight={600}
                  gutterBottom>
                  7-Day Task Trend
                </Typography>
                <Box height={300}>
                  <ResponsiveContainer width="100%"
                    height="100%">
                    <AreaChart data={completionTrend}>
                      <CartesianGrid strokeDasharray="3 3"
                        stroke={theme.palette.divider} />
                      <XAxis dataKey="day"
                        tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <RechartsTooltip />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Area
                        type="monotone"
                        dataKey="created"
                        stackId="1"
                        stroke={theme.palette.primary.main}
                        fill={theme.palette.primary.light}
                        name="Created"
                      />
                      <Area
                        type="monotone"
                        dataKey="completed"
                        stackId="2"
                        stroke={theme.palette.success.main}
                        fill={theme.palette.success.light}
                        name="Completed"
                      />
                      <Area
                        type="monotone"
                        dataKey="overdue"
                        stackId="3"
                        stroke={theme.palette.error.main}
                        fill={theme.palette.error.light}
                        name="Overdue"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* By Category Tab */}
        <TabPanel value={tabValue}
          index={1}>
          <Box
            sx={{ padding: 8 }}>
            <Grid container
              spacing={3}>
              <Grid item
                xs={12}
                md={8}>
                <Typography variant="textMD"
                  fontWeight={600}
                  gutterBottom>
                  Tasks by Category
                </Typography>
                <Box height={400}>
                  <ResponsiveContainer width="100%"
                    height="100%">
                    <BarChart data={tasksByCategory}
                      layout="vertical">
                      <CartesianGrid strokeDasharray="3 3"
                        stroke={theme.palette.divider} />
                      <XAxis type="number"
                        tick={{ fontSize: 10 }} />
                      <YAxis dataKey="name"
                        type="category"
                        width={150}
                        tick={{ fontSize: 10 }} />
                      <RechartsTooltip />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Bar dataKey="completed"
                        stackId="a"
                        fill={theme.palette.success.main}
                        name="Completed" />
                      <Bar dataKey="overdue"
                        stackId="a"
                        fill={theme.palette.error.main}
                        name="Overdue" />
                      <Bar
                        dataKey="total"
                        fill={theme.palette.custom.gray[300]}
                        name="Total (incl. completed)"
                        radius={[0, 4, 4, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>

              <Grid item
                xs={12}
                md={4}>
                <Typography variant="textMD"
                  fontWeight={600}
                  gutterBottom>
                  Category Completion Rates
                </Typography>
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table size="small"
                    stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tasksByCategory.map((cat) => (
                        <TableRow key={cat.name}>
                          <TableCell>{cat.name}</TableCell>
                          <TableCell align="right">
                            <Box display="flex"
                              alignItems="center"
                              justifyContent="flex-end"
                              gap={1}>
                              <LinearProgress
                                variant="determinate"
                                value={cat.completionRate}
                                sx={{
                                  width: 60,
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: theme.palette.custom.gray[200],
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor:
                                      cat.completionRate >= 80
                                        ? theme.palette.success.main
                                        : cat.completionRate >= 50
                                          ? theme.palette.warning.main
                                          : theme.palette.error.main,
                                  },
                                }}
                              />
                              <Typography variant="textXS"
                                sx={{ minWidth: 35 }}>
                                {cat.completionRate}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* By Location Tab */}
        <TabPanel value={tabValue}
          index={2}>
          <Box>
            <Grid container
              spacing={3}>
              <Grid item
                xs={12}>
                <TableHeader>Store Performance</TableHeader>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Store</TableCell>
                        <TableCell align="center">Total Tasks</TableCell>
                        <TableCell align="center">Completed</TableCell>
                        <TableCell align="center">Overdue</TableCell>
                        <TableCell align="center">Completion Rate</TableCell>
                        <TableCell align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {storePerformance.map((store) => (
                        <TableRow key={store.storeNumber}
                          hover>
                          <TableCell>
                            <Box>
                              <Typography variant="textSM"
                                fontWeight={500}>
                                {store.storeName}
                              </Typography>
                              <Typography variant="textXS"
                                color="text.secondary">
                                {' (' + store.storeNumber + ')'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">{store.total}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={store.completed}
                              size="small"
                              sx={{
                                backgroundColor: theme.palette.success.light,
                                color: theme.palette.common.white,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {store.overdue > 0 ? (
                              <Chip
                                label={store.overdue}
                                size="small"
                                sx={{
                                  backgroundColor: theme.palette.error.light,
                                  color: theme.palette.error.dark,
                                }}
                              />
                            ) : (
                              <Typography variant="textXS"
                                color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex"
                              alignItems="center"
                              justifyContent="center"
                              gap={1}>
                              <LinearProgress
                                variant="determinate"
                                value={store.completionRate}
                                sx={{
                                  width: 80,
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: theme.palette.custom.gray[200],
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor:
                                      store.completionRate >= 80
                                        ? theme.palette.success.main
                                        : store.completionRate >= 50
                                          ? theme.palette.warning.main
                                          : theme.palette.error.main,
                                  },
                                }}
                              />
                              <Typography variant="textSM">{store.completionRate}%</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            {store.overdue > 0 ? (
                              <Chip
                                label="Needs Attention"
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            ) : store.completionRate >= 80 ? (
                              <Chip
                                label="On Track"
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            ) : (
                              <Chip
                                label="In Progress"
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter></TableFooter>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Trends & Insights Tab */}
        <TabPanel value={tabValue}
          index={3}>
          <Box
            sx={{ padding: 8 }}>
            <Grid container
              spacing={3}>
              {/* Priority Escalations */}
              <Grid item
                xs={12}
                md={6}>
                <Typography variant="textMD"
                  fontWeight={600}
                  gutterBottom>
                  Priority Escalations
                </Typography>
                <Typography variant="textSM"
                  color="text.secondary"
                  mb={2}>
                  Tasks where priority has been increased
                </Typography>
                {priorityEscalations.length > 0 ? (
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small"
                      stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Task</TableCell>
                          <TableCell>Default</TableCell>
                          <TableCell>Current</TableCell>
                          <TableCell>Change</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {priorityEscalations.map((task) => (
                          <TableRow key={task.taskId}>
                            <TableCell>
                              <Typography variant="textSM"
                                noWrap
                                sx={{ maxWidth: 150 }}>
                                {task.title}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={task.defaultPriority}
                                size="small"
                                sx={{
                                  backgroundColor: PRIORITY_COLORS[task.defaultPriority] + '30',
                                  color: PRIORITY_COLORS[task.defaultPriority],
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={task.currentPriority}
                                size="small"
                                sx={{
                                  backgroundColor: PRIORITY_COLORS[task.currentPriority] + '30',
                                  color: PRIORITY_COLORS[task.currentPriority],
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {task.escalated ? (
                                <TrendingUpIcon sx={{ color: theme.palette.error.main, fontSize: 18 }} />
                              ) : (
                                <TrendingDownIcon sx={{ color: theme.palette.success.main, fontSize: 18 }} />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height={200}
                    sx={{ backgroundColor: theme.palette.custom.gray[50], borderRadius: 2 }}
                  >
                    <Typography color="text.secondary">No priority escalations found</Typography>
                  </Box>
                )}
              </Grid>

              {/* Key Insights */}
              <Grid item
                xs={12}
                md={6}>
                <Typography variant="textMD"
                  fontWeight={600}
                  gutterBottom>
                  Key Insights
                </Typography>
                <Box display="flex"
                  flexDirection="column"
                  gap={2}>
                  <Card variant="outlined"
                    sx={{ p: 2 }}>
                    <Box display="flex"
                      alignItems="center"
                      gap={2}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: '50%',
                          height: '36px',
                          width: '36px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          backgroundColor: theme.palette.success.light + '50',
                        }}
                      >
                        <CheckCircleOutlineIcon sx={{ color: theme.palette.success.main }} />
                      </Box>
                      <Box>
                        <Typography variant="textSM"
                          fontWeight={600}
                          style={{ marginRight: 4 }}>
                          Best Performing Category
                        </Typography>
                        <Typography variant="textXS"
                          color="text.secondary">
                          {tasksByCategory.length > 0
                            ? `${tasksByCategory.reduce((a, b) => a.completionRate > b.completionRate ? a : b).name} with ${tasksByCategory.reduce((a, b) => a.completionRate > b.completionRate ? a : b).completionRate}% completion`
                            : 'No data available'}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>

                  <Card variant="outlined"
                    sx={{ p: 2 }}>
                    <Box display="flex"
                      alignItems="center"
                      gap={2}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: '50%',
                          height: '36px',
                          width: '36px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          backgroundColor: theme.palette.warning.light + '50',
                        }}
                      >
                        <WarningAmberIcon sx={{ color: theme.palette.warning.main }} />
                      </Box>
                      <Box>
                        <Typography variant="textSM"
                          fontWeight={600}
                          style={{ marginRight: 4 }}>
                          Category Needing Attention
                        </Typography>
                        <Typography variant="textXS"
                          color="text.secondary">
                          {tasksByCategory.length > 0
                            ? `${tasksByCategory.reduce((a, b) => a.completionRate < b.completionRate ? a : b).name} with ${tasksByCategory.reduce((a, b) => a.completionRate < b.completionRate ? a : b).completionRate}% completion`
                            : 'No data available'}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>

                  <Card variant="outlined"
                    sx={{ p: 2 }}>
                    <Box display="flex"
                      alignItems="center"
                      gap={2}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: '50%',
                          height: '36px',
                          width: '36px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          backgroundColor: theme.palette.info.light + '50',
                        }}
                      >
                        <AccessTimeIcon sx={{ color: theme.palette.info.main }} />
                      </Box>
                      <Box>
                        <Typography variant="textSM"
                          fontWeight={600}
                          style={{ marginRight: 4 }}>
                          Average On-Time Completion
                        </Typography>
                        <Typography variant="textXS"
                          color="text.secondary">
                          {completionMetrics.onTimeRate}% of tasks completed before due date
                        </Typography>
                      </Box>
                    </Box>
                  </Card>

                  <Card variant="outlined"
                    sx={{ p: 2 }}>
                    <Box display="flex"
                      alignItems="center"
                      gap={2}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: '50%',
                          height: '36px',
                          width: '36px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          backgroundColor: theme.palette.primary.light + '50',
                        }}
                      >
                        <AssignmentIcon sx={{ color: theme.palette.primary.main }} />
                      </Box>
                      <Box>
                        <Typography variant="textSM"
                          fontWeight={600}
                          style={{ marginRight: 4 }}>
                          High Priority Tasks
                        </Typography>
                        <Typography variant="textXS"
                          color="text.secondary">
                          {filteredTasks.filter(t => t.priorityId === 3).length} tasks currently marked as high priority
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Box>
              </Grid>

              {/* Colleague Workload (if data available) */}
              <Grid item
                xs={12}>
                <Typography variant="textMD"
                  fontWeight={600}
                  gutterBottom>
                  Task Distribution by Assignment Type
                </Typography>
                <Box height={300}>
                  <ResponsiveContainer width="100%"
                    height="100%">
                    <BarChart
                      data={[
                        { type: 'Assigned to Location', count: filteredTasks.filter(t => t.targetType === 'Location').length || filteredTasks.length * 0.6 },
                        { type: 'Assigned to Team', count: filteredTasks.filter(t => t.targetType === 'Team').length || filteredTasks.length * 0.4 },
                      ]}
                    >
                      <CartesianGrid strokeDasharray="3 3"
                        stroke={theme.palette.divider} />
                      <XAxis dataKey="type"
                        tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <RechartsTooltip />
                      <Bar dataKey="count"
                        fill={theme.palette.primary.main}
                        radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </Section>
    </PageArea >
  );
};

export default TaskReportingDashboard;
